/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { Icon20Verified } from '@vkontakte/icons';
import classNames from 'clsx';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import { ENABLE_FULL_RESPONSIVE } from 'reactApp/appHelpers/configHelpers';
import { BREAKPOINT_MD, BREAKPOINT_SM } from 'reactApp/constants/breakpoints';
import { PromoSelectors } from 'reactApp/modules/promo/promo.selectors';
import { EPromoType } from 'reactApp/modules/promo/promo.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { DropdownList, DropdownTheme } from 'reactApp/ui/DropdownList/DropdownList';
import { FilterLabel } from 'reactApp/ui/Header/Search/FilterLabel/FilterLabel';
import { typesOptionsList } from 'reactApp/ui/Header/Search/Search.data';
import { useSearchContent } from 'reactApp/ui/Header/Search/useSearchContent';
import { useWindowWidth } from 'reactApp/ui/VirtualList/VirtualList.hooks';
import { SearchIcon } from 'reactApp/ui/VKUIIcons';
import { Input } from 'semantic-ui-react';

import { SearchTooltip } from '../SearchTooltip/SearchTooltip';
import { SearchTooltipTypes } from '../SearchTooltip/SearchTooltip.data';
import { getSearchTooltipType } from '../SearchTooltip/SearchTooltip.helpers';
import { useTooltipState } from '../SearchTooltip/SearchTooltip.hooks';
import styles from './Search.css';

interface IProps {
    dropdownAvailable: boolean;
    toggleSearch: (state: boolean) => void;
    isExpanded: boolean;
    onlyExpanded?: boolean;
    storage: EStorageType;
    isResponsiveEnable?: boolean;
}

export const Search = memo(({ dropdownAvailable, toggleSearch, isExpanded, storage, onlyExpanded = false, isResponsiveEnable }: IProps) => {
    const [searchInputRef, setInputRef] = useState(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const tooltipType = getSearchTooltipType();
    const onRefChange = useCallback((node) => {
        if (node && node.inputRef && node.inputRef.current) {
            setInputRef(node.inputRef);
        }
    }, []);

    // tempexp_SRCH-70119-next-line
    const searchTooltipPromo = useSelector(PromoSelectors.getPromo(EPromoType.searchTooltip));
    const [isTooltipVisible, onTooltipClose] = useTooltipState({
        promo: searchTooltipPromo,
        visibilityValue: !!searchTooltipPromo && !!(tooltipType && !(isExpanded || onlyExpanded) && searchInputRef),
        tooltipType,
    });

    const {
        isActive,
        isFocus,
        value,
        searchOption,
        placeholder,
        onSearchClick,
        onInputClick,
        onDocumentClick,
        onInputChange,
        onKeyUp,
        setActiveInput,
        chooseSearchType,
        removeSearchType,
        removeFocus,
    } = useSearchContent({ toggleSearch, rootRef, isExpanded, onlyExpanded, storage, dropdownRef });
    const windowWidth = useWindowWidth();
    const isSearchActive = searchOption && isActive;
    const renderFilterLabel = ENABLE_FULL_RESPONSIVE ? windowWidth > BREAKPOINT_MD && isSearchActive : isSearchActive;
    const clearFilterByClick = ENABLE_FULL_RESPONSIVE ? windowWidth <= BREAKPOINT_MD && isSearchActive : false;
    const renderDropdown =
        (ENABLE_FULL_RESPONSIVE && windowWidth > BREAKPOINT_MD) || !ENABLE_FULL_RESPONSIVE
            ? isFocus && !searchOption && dropdownAvailable
            : isFocus;

    useEffect(() => {
        document.addEventListener('click', onDocumentClick, { capture: true });

        return () => {
            document.removeEventListener('click', onDocumentClick, { capture: true });
        };
    }, [onDocumentClick]);

    const optionsList = useMemo(
        () =>
            typesOptionsList.map((item) => ({
                ...item,
                onClick: () => {
                    if (clearFilterByClick && searchOption?.id === item.id) {
                        removeSearchType();
                        return;
                    }
                    chooseSearchType(item.id);
                },
            })),
        [clearFilterByClick, searchOption?.id, chooseSearchType, removeSearchType]
    );

    const renderDropdownItem = useCallback(
        ({ id, icon, text }) => (
            <div className={styles.dropdownItem}>
                <div className={styles.dropdownIcon}>{icon}</div>
                {text}
                {ENABLE_FULL_RESPONSIVE && isActive && searchOption?.id === id && (
                    <div className={styles.dropdownCheckIcon}>
                        <Icon20Verified />
                    </div>
                )}
            </div>
        ),
        [searchOption, isActive]
    );

    const calcDropdownPosition = useCallback(() => {
        let posX = 0;
        let posY = 0;

        if (rootRef.current) {
            const elRect = rootRef.current.getBoundingClientRect();

            posY = elRect.top + elRect.height + window.scrollY + 8;
            posX = elRect.left + window.scrollX + 40;
        }

        return { posY, posX };
    }, []);

    const searchSubmitButton = useMemo(() => {
        if (isResponsiveEnable && !isActive) {
            return null;
        }

        return (
            <div
                onClick={onSearchClick}
                className={classNames(styles.search, {
                    [styles.searchResponsive]: ENABLE_FULL_RESPONSIVE,
                })}
            >
                {!isActive || (ENABLE_FULL_RESPONSIVE && windowWidth < BREAKPOINT_SM) ? <SearchIcon /> : 'Найти'}
            </div>
        );
    }, [isActive, onSearchClick, isResponsiveEnable, windowWidth]);

    return (
        <CSSTransition
            in={isExpanded}
            timeout={200}
            classNames={{
                exit: styles.animation_exit,
                exitActive: styles.animation_exit_active,
                enter: styles.animation_appear,
                enterActive: styles.animation_appear_active,
            }}
            onEntered={setActiveInput}
        >
            <div
                className={classNames(styles.root, {
                    [styles.root_expand]: isExpanded || onlyExpanded,
                    [styles.root_active]: isActive,
                    [styles.root_my_team]: isResponsiveEnable && !isActive,
                    [styles.root_responsive]: ENABLE_FULL_RESPONSIVE,
                })}
                ref={rootRef}
            >
                {isActive && (
                    <SearchIcon
                        className={classNames(styles.searchIcon, {
                            [styles.searchIconResponsive]: ENABLE_FULL_RESPONSIVE,
                        })}
                    />
                )}
                {renderFilterLabel && (
                    <FilterLabel id={searchOption.id} text={searchOption.text} icon={searchOption.icon} removeFilter={removeSearchType} />
                )}
                {isResponsiveEnable && !isExpanded ? (
                    <>
                        <SearchIcon onClick={onInputClick} />
                    </>
                ) : (
                    <Input
                        className={classNames(styles.input, {
                            [styles.inputResponsive]: ENABLE_FULL_RESPONSIVE,
                        })}
                        placeholder={placeholder}
                        onClick={onInputClick}
                        onChange={onInputChange}
                        value={value}
                        onKeyUp={onKeyUp}
                        focus={isFocus}
                        ref={onRefChange}
                    />
                )}
                {searchSubmitButton}
                {renderDropdown && (
                    <DropdownList
                        dataQAId="search-options"
                        list={optionsList}
                        calcPosition={calcDropdownPosition}
                        close={removeFocus}
                        renderItem={renderDropdownItem}
                        parentRef={rootRef}
                        gaId="search"
                        closeOnScroll
                        theme={DropdownTheme.medium}
                        rootRef={dropdownRef}
                        className={ENABLE_FULL_RESPONSIVE ? styles.searchDropdown : undefined}
                    />
                )}
                {isTooltipVisible && (
                    <SearchTooltip targetRef={searchInputRef} type={tooltipType as SearchTooltipTypes} onClose={onTooltipClose} />
                )}
            </div>
        </CSSTransition>
    );
});

Search.displayName = 'Search';
